import { MouseEvent, ReactNode, RefObject, useRef, useState } from "react";
import {
  ColumnActionsMode,
  ContextualMenu,
  IColumn,
  IContextualMenuItem,
  IDetailsList,
} from "@fluentui/react";

/**
 * Options for {@link useResizableColumns}.
 */
export interface UseResizableColumnsOptions {
  /** Pixel step used by "Widen" / "Narrow". Defaults to 60. */
  step?: number;
  /** Minimum column width the menu will let a user shrink to. Defaults to 60. */
  minWidth?: number;
  widenLabel?: string;
  narrowLabel?: string;
  resetLabel?: string;
}

/**
 * The wire-up returned by {@link useResizableColumns}. Spread onto a
 * `DetailsList` / `ShimmeredDetailsList` / `SortableDetailsList`:
 *
 *   ```tsx
 *   const resize = useResizableColumns(baseColumns);
 *   return (
 *     <>
 *       {resize.headerContextMenu}
 *       <SortableDetailsList
 *         componentRef={resize.detailsListRef}
 *         columns={resize.columns}
 *         onColumnHeaderContextMenu={resize.onColumnHeaderContextMenu}
 *         onColumnResize={resize.onColumnResize}
 *         ...
 *       />
 *     </>
 *   );
 *   ```
 */
export interface UseResizableColumnsResult {
  /** Columns with `columnActionsMode: hasDropdown` set on every resizable column. */
  columns: IColumn[];
  /** Attach as `componentRef` on the DetailsList so we can drive `updateColumn`. */
  detailsListRef: RefObject<IDetailsList>;
  /** Wire to the DetailsList's `onColumnHeaderContextMenu` prop. */
  onColumnHeaderContextMenu: (
    column?: IColumn,
    ev?: MouseEvent<HTMLElement>
  ) => void;
  /** Wire to the DetailsList's `onColumnResize` prop so mouse-drag stays in sync. */
  onColumnResize: (column?: IColumn, newWidth?: number) => void;
  /** Render this next to the DetailsList — it holds the resize menu. */
  headerContextMenu: ReactNode;
}

const DEFAULT_STEP = 60;
const DEFAULT_MIN_WIDTH = 60;

/**
 * Gives a Fluent v8 `DetailsList` a keyboard-accessible column resize
 * mechanism.
 *
 * Fluent v8 renders the mouse resize gripper as a non-focusable div with no
 * ARIA role and no keyboard handler, which fails a11y review (there is no
 * keyboard alternative to resize columns).
 *
 * This hook attaches a `ColumnActionsMode.hasDropdown` chevron to every
 * resizable column and opens a `ContextualMenu` with "Widen" / "Narrow" /
 * "Reset width" actions on `onColumnHeaderContextMenu`. That menu is:
 *   - Reachable with the mouse via the chevron.
 *   - Reachable with the keyboard via Shift+F10 (or the Context Menu key)
 *     while the column header is focused — this is what the accessibility
 *     scanners look for.
 *
 * Width is applied via `IDetailsList.updateColumn(column, { width })` (the
 * same internal path the mouse drag uses); mutating `minWidth` / `maxWidth`
 * on the column props does not work reliably because DetailsList keeps its
 * own private `_columnOverrides` map that shadows those values.
 */
export function useResizableColumns(
  baseColumns: IColumn[],
  options: UseResizableColumnsOptions = {}
): UseResizableColumnsResult {
  const step = options.step ?? DEFAULT_STEP;
  const minWidth = options.minWidth ?? DEFAULT_MIN_WIDTH;

  const detailsListRef = useRef<IDetailsList>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [headerMenu, setHeaderMenu] = useState<{
    target: HTMLElement;
    columnKey: string;
  } | null>(null);

  const onColumnHeaderContextMenu = (
    column?: IColumn,
    ev?: MouseEvent<HTMLElement>
  ) => {
    if (
      !column ||
      !ev ||
      !column.isResizable ||
      column.columnActionsMode === ColumnActionsMode.disabled
    ) {
      return;
    }
    ev.preventDefault();
    setHeaderMenu({
      target: ev.currentTarget as HTMLElement,
      columnKey: column.key,
    });
  };

  const onColumnResize = (column?: IColumn, newWidth?: number) => {
    if (column && typeof newWidth === "number") {
      setColumnWidths(prev => ({ ...prev, [column.key]: newWidth }));
    }
  };

  const applyColumnWidth = (column: IColumn, width: number) => {
    const clamped = Math.max(minWidth, Math.round(width));
    setColumnWidths(prev => ({ ...prev, [column.key]: clamped }));
    detailsListRef.current?.updateColumn(column, { width: clamped });
  };

  const getCurrentWidth = (col: IColumn): number =>
    columnWidths[col.key] ?? col.calculatedWidth ?? col.currentWidth ?? col.minWidth;

  const columns: IColumn[] = baseColumns.map(col =>
    col.isResizable
      ? { ...col, columnActionsMode: ColumnActionsMode.hasDropdown }
      : col
  );

  const activeColumn = headerMenu
    ? columns.find(c => c.key === headerMenu.columnKey)
    : undefined;

  const items: IContextualMenuItem[] = activeColumn
    ? [
        {
          key: "widen",
          text: options.widenLabel ?? "Widen column",
          iconProps: { iconName: "DoubleChevronRight8" },
          onClick: () => {
            applyColumnWidth(activeColumn, getCurrentWidth(activeColumn) + step);
          },
        },
        {
          key: "narrow",
          text: options.narrowLabel ?? "Narrow column",
          iconProps: { iconName: "DoubleChevronLeft8" },
          disabled: getCurrentWidth(activeColumn) <= minWidth,
          onClick: () => {
            applyColumnWidth(activeColumn, getCurrentWidth(activeColumn) - step);
          },
        },
        {
          key: "reset",
          text: options.resetLabel ?? "Reset width",
          iconProps: { iconName: "Refresh" },
          onClick: () => {
            applyColumnWidth(activeColumn, activeColumn.minWidth);
          },
        },
      ]
    : [];

  const headerContextMenu =
    headerMenu && items.length > 0 ? (
      <ContextualMenu
        items={items}
        target={headerMenu.target}
        onDismiss={() => setHeaderMenu(null)}
        onItemClick={() => setHeaderMenu(null)}
      />
    ) : null;

  return {
    columns,
    detailsListRef,
    onColumnHeaderContextMenu,
    onColumnResize,
    headerContextMenu,
  };
}
